import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { ENDPOINTS } from "../../services/endpoints";
import { IMAGES } from "../../constants/image";
import signupConfig from "../../config/forms/signup.json";
import { validateValue } from "../../utils/validation";
import PhoneNumberInput from "../common/PhoneNumberInput";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";

interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
}

interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  validation?: FieldValidation;
  options?: { label: string; value: string }[];
  visibleWhen?: {
    field: string;
    values?: string[];
  };
  dynamicPlaceholder?: Record<string, string>;
}
const Signup: React.FC = () => {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showPopup = usePopup();

  const fields = signupConfig.fields as FormField[];
  const ICON_MAP: Record<string, string> = {
    name: IMAGES.ICONS.NAME,
    email: IMAGES.ICONS.EMAIL,
    contactNumber: IMAGES.ICONS.PHONE,
    profession: IMAGES.ICONS.PORTFOLIO,
    description: IMAGES.ICONS.DESCRIPTION,
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      if (!isFieldVisible(field)) return;

      const value = formData[field.name];

      const error = validateValue(value, {
        label: field.label,
        ...(field.validation || {}),
      });

      if (error) newErrors[field.name] = error;
    });

    return newErrors;
  };

  const validateField = (name: string, value: string) => {
    const field = fields.find((f) => f.name === name);
    if (!field || !isFieldVisible(field)) return "";

    const error = validateValue(value, {
      label: field.label,
      ...(field.validation || {}),
    });

    return error || "";
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    //update form data
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    //validate only this field
    const errorMsg = validateField(name, value);

    setErrors((prev: any) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handlePhoneChange = (
    name: string,
    value: string,
    countryCode: string,
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
      contactCountryCode: countryCode,
    }));

    setErrors((prev: any) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };
  //Visibility Check
  const isFieldVisible = (field: FormField) => {
    if (!field.visibleWhen) return true;

    const value = formData[field.visibleWhen.field];
    if (!value) return false;

    if (field.visibleWhen.values) {
      return field.visibleWhen.values.includes(value);
    }

    return true;
  };

  //Dynamic Placeholder
  const getPlaceholder = (field: FormField) => {
    if (field.dynamicPlaceholder) {
      const selected = formData["profession"];
      return field.dynamicPlaceholder[selected] || field.placeholder;
    }
    return field.placeholder;
  };

  //Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    // Map payload
    const data = {
      name: formData.name,
      email: formData.email,
      country_code: formData.contactCountryCode || "+91",
      contact_no: formData.contactNumber,
      occupation: formData.profession,
      description: formData.description,
    };

    try {
      const response = await api.post(ENDPOINTS.AUTH.SIGNUP, data);
      const apiData = response?.data;
      const messageResponse = extractMessageResponse(apiData, "Signup completed successfully.", 200);
      const responseError =
        apiData?.error?.[0] ||
        apiData?.message?.error?.[0] ||
        (typeof apiData?.message === "string" &&
        apiData?.message.toLowerCase().includes("already registered")
          ? apiData?.message
          : "");

      if (responseError) {
        setErrors((prev: any) => ({
          ...prev,
          apiError: responseError,
        }));
        showPopup({
          status: messageResponse.status,
          message: responseError,
          messageType: messageResponse.messageType,
          messageCode: messageResponse.messageCode,
        });
      } else {
        showPopup({
          status: messageResponse.status,
          message: messageResponse.message,
          messageType: messageResponse.messageType,
          messageCode: messageResponse.messageCode,
          onOk: () => navigate("/login"),
        });
        //reset form
        setFormData({});
      }
    } catch (error: any) {
      const apiError = error?.response?.data;
      const messageResponse = extractMessageResponse(apiError, "Signup failed", 500);
      const message = messageResponse.message;

      setErrors((prev: any) => ({
        ...prev,
        apiError: message,
      }));
      showPopup({
        status: messageResponse.status,
        message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({});
    setErrors({});
  };

  return (
    <div
      className="signup-wrapper"
      // style={{ backgroundImage: `url(${ICON_MAP["signup-bg"]})` }}
    >
      <div className="signup-background">
        <div className="signup-card-container">
          <div className="card signup-card shadow">
            <div className=" card-body">
              {/* Heading */}
              <h3 className="text-center fw-bold mb-1 text-white">
                Create Account
              </h3>
              <p className="text-center text-white mb-3">
                Please fill in your details to get started
              </p>

              <form onSubmit={handleSubmit}>
                {fields.map((field) => {
                  if (!isFieldVisible(field)) return null;

                  const value = formData[field.name] || "";
                  const error = errors[field.name];

                  return (
                    <div className="mb-3 position-relative" key={field.name}>
                      {/* Label */}
                      {field.label && (
                        <label className="form-label">{field.label}</label>
                      )}

                      {/* Input / Select */}
                      <div className="signup-input-group">
                        {field.type === "select" ? (
                          <select
                            name={field.name}
                            className={`form-select custom-input ${error ? "is-invalid" : ""}`}
                            value={value}
                            onChange={handleChange}
                          >
                            <option value="">
                              {field.placeholder || "Select"}
                            </option>

                            {field.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field.name === "contactNumber" ? (
                          <PhoneNumberInput
                            name={field.name}
                            value={value}
                            countryCode={formData.contactCountryCode}
                            placeholder={getPlaceholder(field)}
                            hasError={Boolean(error)}
                            onChange={handlePhoneChange}
                          />
                        ) : (
                          <input
                            type={field.type}
                            name={field.name}
                            className={`form-control custom-input ${error ? "is-invalid" : ""}`}
                            placeholder={getPlaceholder(field)}
                            value={value}
                            onChange={handleChange}
                          />
                        )}

                        {/* Icon */}
                        {ICON_MAP[field.name] && field.name !== "contactNumber" && (
                          <img
                            src={ICON_MAP[field.name]}
                            alt={field.name}
                            className="input-icon"
                          />
                        )}
                      </div>

                      {/* Error */}
                      {error && (
                        <div className="invalid-feedback d-block">{error}</div>
                      )}
                    </div>
                  );
                })}

                {/* Buttons */}
                <div className="d-flex gap-2 signup-action-row">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 signup-action-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Sign Up"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary w-100 signup-action-btn"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>

              {/* Footer */}
              <div className="text-center mt-4">
                <span className="text-muted">Already have an account? </span>
                <Link to="/login" className="login-link">
                  Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Signup;
